import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import { productStatus, subscriptionPeriod } from "../../../enums";

import {
  useHookForm,
  useMerchantApiMutation,
  useMerchantApiQueryWithParams,
} from "../../../hooks";
import {
  IEditProduct,
  IHookForm,
  IModalFormWithTableProps,
  IPageAndSearchRequest,
  IViewBankAccount,
  PagedMeta,
  IViewProduct,
  IFormWithTableProps,
} from "../../../interfaces-and-types";
import { convertObjectToFormData, globalHandlerUtils } from "../../../utils";

import { View } from "./View";

export interface IEditProductFormViewProps
  extends IHookForm<IEditProduct>,
    IModalFormWithTableProps {
  isLoadingBankAccounts: boolean;
  activePayoutTags: IViewBankAccount[];
  formValues: IEditProduct;
  saveProductIcon: (e: React.ChangeEvent<HTMLInputElement>) => void;
  actualImage?: string;
}

export const EditProductForm = (props: IFormWithTableProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const closeModal = () => {
    // simply go back if already on the payments page
    // if (location.pathname.indexOf("payments") >= -1) navigate(-1);
    // else navigate("/payments");
    navigate(-1);
    setActualImage(undefined);
    reset();
  };

  const { id } = useParams();

  const {
    isLoading: isLoadingModalData,
    isSuccess: isSuccessModalData,
    isError: isErrorModalData,
    data: productToEdit,
  } = useMerchantApiQueryWithParams<{ id: string }, IViewProduct>(
    [
      "getProductById",
      {
        id: id!, // NOTE: use this to ensure that react query caches with the correct queryKey
      },
    ],
    {
      enabled: !!id,
    },
    (urlTemplate) => urlTemplate.replace("id", id!)
  );

  useEffect(() => {
    if (isSuccessModalData) {
      reset({
        ...productToEdit,
        product_icon: null,
      });

      productToEdit && setActualImage(productToEdit?.product_icon ?? undefined);
    }
  }, [isSuccessModalData]);

  const {
    isLoading: isLoadingBankAccounts,
    isSuccess: isSuccessBankAccounts,
    data: withdrawalBankAccounts,
  } = useMerchantApiQueryWithParams<
    IPageAndSearchRequest,
    IViewBankAccount[],
    PagedMeta
  >([
    "getBankAccounts",
    {
      page: 1,
      page_size: 5000,
      search: null,
    },
  ]);

  const { isSuccess: isSuccessSaveProduct, mutate: editProduct } =
    useMerchantApiMutation<FormData, IViewProduct>(
      "editProduct",
      [],
      {
        headers: { "content-type": "multipart/form-data" },
      },
      (urlTemplate) => urlTemplate.replace("id", id!)
    ).usePutMutation;

  useEffect(() => {
    if (isSuccessSaveProduct) {
      props.refreshTable();
      closeModal();
      reset();
    }
  }, [isSuccessSaveProduct]);

  const initialFormValues: IEditProduct = {
    id: "",
    product_icon: null,
    product_name: "",
    product_description: "",
    payout_tag: null,
    price: 0,
    currency: "",
    is_subscription_product: false,
    subscription_period: subscriptionPeriod.daily,
    status: productStatus.active,

    // qty tracking
    enable_quantity_tracking: false,
    enable_product_feature_based_pricing: false,

    // product_features: [],
    // product_feature_value_quantities: [],
  };

  const editProductSchema: yup.SchemaOf<IEditProduct> = yup
    .object({
      id: yup.string().required(),
      product_icon: yup.mixed().nullable().optional(),
      product_name: yup.string().required("Product Name is required"),
      product_description: yup.string().nullable().optional(),
      payout_tag: yup.string().nullable().optional(),
      price: yup
        .number()
        .transform((_value, originalValue) =>
          !originalValue
            ? 0
            : Number(originalValue.toString().replace(/,/g, ""))
        )
        .required("price is required")
        .positive("Amount must be greater than zero"),
      currency: yup.string().required("Currency is required"),
      is_subscription_product: yup
        .boolean()
        .required("Subscription Type is required"),
      subscription_period: yup
        .mixed<subscriptionPeriod>()
        .oneOf(Object.values(subscriptionPeriod))
        .required("Subscription Period is required"),
      status: yup
        .mixed<productStatus>()
        .oneOf(Object.values(productStatus))
        .required("status is required"),

      // qty tracking
      enable_quantity_tracking: yup
        .boolean()
        .required("Quantity tracking status is required"),
      enable_product_feature_based_pricing: yup
        .boolean()
        .required("Feature based pricing status is required"),
    })
    .required();

  const [productIconFile, setProductIconFile] = useState<File | null>(null);
  const [actualImage, setActualImage] = useState<string | undefined>(undefined);

  const saveProductIcon = (e: React.ChangeEvent<HTMLInputElement>) => {
    let reader = new FileReader();
    reader.onload = function (event: any) {
      // (document.querySelector(".image-previewer")! as HTMLImageElement).src =
      //   event.target.result;
      setActualImage(event.target.result);
    };
    if (e.target.files && e.target.files[0]) {
      setProductIconFile(e.target.files[0]);
      reader.readAsDataURL(e.target.files[0]);
      document
        .querySelector(".image-previewer")!
        .classList.add("remove-border");
    }
  };

  const handleApiSubmit = (data: IEditProduct) => {
    const stateAsFormData = convertObjectToFormData(initialFormValues, data, [
      { uploadFileName: "product_icon", file: productIconFile },
    ]);

    editProduct(stateAsFormData);
  };

  const handleError = (errors: any) => globalHandlerUtils.handleError(errors);

  const { onFormSubmit, errors, register, control, reset, watch } = useHookForm(
    {
      initialFormValues,
      validationSchema: editProductSchema,
      onValid: handleApiSubmit,
      onInvalid: handleError,
    }
  );

  const formValues = watch();

  const activePayoutTagBankAccounts =
    withdrawalBankAccounts?.filter(
      (acc) => !acc.is_disabled && acc.bank_account_tags.length
    ) || [];

  return (
    <View
      // modal management
      isLoadingModalData={isLoadingModalData}
      isErrorModalData={isErrorModalData}
      isModalOpen={!!id}
      closeModal={closeModal}
      // form management
      refreshTable={props.refreshTable}
      isLoadingBankAccounts={isLoadingBankAccounts}
      activePayoutTags={activePayoutTagBankAccounts}
      handleSubmit={onFormSubmit}
      saveProductIcon={saveProductIcon}
      register={register}
      errors={errors}
      formValues={formValues}
      actualImage={actualImage}
    />
  );
};
